import React, { useEffect, useState } from "react";
import { Plus, Plane, Check, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Badge, Loading, Modal, fmtDate, fmtMoney, EmptyState } from "../components/ui";

export default function Travel() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ purpose: "", fromCity: "", toCity: "", fromDate: "", toDate: "", estimatedCost: "" });
  const canApprove = ["SUPERADMIN", "HR_ADMIN", "MANAGER"].includes(user?.role);

  const load = () => api.get("/travel").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const add = async (e) => { e.preventDefault(); await api.post("/travel", form); setOpen(false); setForm({ purpose: "", fromCity: "", toCity: "", fromDate: "", toDate: "", estimatedCost: "" }); load(); };
  const decide = async (id, status) => { await api.put(`/travel/${id}`, { status }); load(); };
  if (!items) return <Loading />;

  return (
    <div>
      <PageHeader title="Travel" subtitle="Business travel requests">
        <button data-testid="add-travel" onClick={() => setOpen(true)} className="btn-accent"><Plus size={16} /> New Request</button>
      </PageHeader>
      {items.length === 0 ? <EmptyState icon={Plane} title="No travel requests" /> : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {items.map((t) => (
            <Card key={t.id} className="p-5" data-testid={`travel-${t.id}`}>
              <div className="flex items-start justify-between">
                <div><p className="font-semibold text-slate-800 dark:text-slate-100">{t.employeeName}</p><p className="text-xs text-slate-400">{t.fromCity} → {t.toCity}</p></div>
                <Badge value={t.status} />
              </div>
              <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">{t.purpose}</p>
              <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
                <span>{fmtDate(t.fromDate)} – {fmtDate(t.toDate)}</span>
                <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">{fmtMoney(t.estimatedCost)}</span>
              </div>
              {canApprove && t.status === "PENDING" && (
                <div className="mt-4 flex gap-2">
                  <button data-testid={`travel-approve-${t.id}`} onClick={() => decide(t.id, "APPROVED")} className="btn-outline flex-1 text-emerald-600"><Check size={14} /> Approve</button>
                  <button data-testid={`travel-reject-${t.id}`} onClick={() => decide(t.id, "REJECTED")} className="btn-outline flex-1 text-red-600"><X size={14} /> Reject</button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title="Travel Request"
        footer={<><button onClick={() => setOpen(false)} className="btn-outline">Cancel</button><button form="tr-form" className="btn-accent">Submit</button></>}>
        <form id="tr-form" onSubmit={add} className="space-y-4">
          <div><label className="label">Purpose *</label><input required data-testid="tr-purpose" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} className="input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">From</label><input data-testid="tr-from" value={form.fromCity} onChange={(e) => setForm({ ...form, fromCity: e.target.value })} className="input" /></div>
            <div><label className="label">To</label><input data-testid="tr-to" value={form.toCity} onChange={(e) => setForm({ ...form, toCity: e.target.value })} className="input" /></div>
            <div><label className="label">From Date</label><input type="date" value={form.fromDate} onChange={(e) => setForm({ ...form, fromDate: e.target.value })} className="input" /></div>
            <div><label className="label">To Date</label><input type="date" value={form.toDate} onChange={(e) => setForm({ ...form, toDate: e.target.value })} className="input" /></div>
          </div>
          <div><label className="label">Estimated Cost (₹)</label><input type="number" data-testid="tr-cost" value={form.estimatedCost} onChange={(e) => setForm({ ...form, estimatedCost: e.target.value })} className="input" /></div>
        </form>
      </Modal>
    </div>
  );
}
